import type { IOmit } from '@sixui/ui';
import { Button, Dialog, IDialogProps, useId } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { PollSelector } from '~/components/organisms/fields/PollSelector';

export type IPollMdxDirectiveDialogFormValues = {
  pollId?: string;
};

export type IPollMdxDirectiveDialogProps = IOmit<
  IDialogProps,
  'headline' | 'actions' | 'children'
> & {
  appId: string;
  onSubmit?: (
    values: IPollMdxDirectiveDialogFormValues,
    event?: React.BaseSyntheticEvent,
  ) => void;
  defaultValues?: IPollMdxDirectiveDialogFormValues;
};

const styles = stylex.create({
  host: {
    minWidth: 480,
  },
});

export const PollMdxDirectiveDialogForm: React.FC<
  IPollMdxDirectiveDialogProps
> = (props) => {
  const { sx, appId, onSubmit, defaultValues, ...other } = props;
  const { t } = useTranslation();
  const form = useForm<IPollMdxDirectiveDialogFormValues>({
    defaultValues,
  });
  const formId = useId();

  return (
    <Dialog
      {...other}
      sx={[styles.host, sx]}
      headline={t('PollMdxDirectiveDialogForm.title')}
      actions={({ close }) => (
        <>
          <Button
            variant="text"
            onClick={close}
            disabled={form.formState.isSubmitting}
            form={formId}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSubmit ? form.handleSubmit(onSubmit) : undefined}
            form={formId}
          >
            {t('common.insert')}
          </Button>
        </>
      )}
    >
      <ContentBlock.FormBody
        id={formId}
        form={form}
        onSubmit={onSubmit}
        hideActionBar={true}
        extended
      >
        <FormSection>
          <FormField
            as={PollSelector}
            forwardError
            control={form.control}
            name="pollId"
            rules={{ required: true }}
            appId={appId}
          />
        </FormSection>
      </ContentBlock.FormBody>
    </Dialog>
  );
};
